import React, { useContext, useRef, useEffect } from 'react';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';
import { cfetch } from '@confluence/network';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';

import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';

import { ImportStateContext } from '../../ImportStateContextProvider.tsx';
import { ImportModalTypes } from '../../Modals/ImportModalTypes.ts';
import { OneDrivePicker } from '../../../services/OneDriveAPIs/OneDrivePicker.ts';
import { useIntl } from 'react-intl';

// oxlint-disable-next-line import/no-cycle
import { ActiveImports, IMPORT_FOLDER_NAMES } from '../importStrategyDefinitions.ts';
import { useModals } from '../../../hooks/useModals.ts';
import type { OneDriveFileType } from '../../../services/OneDriveAPIs/OneDriveTypes.ts';
import { OneDriveErrorStatuses } from '../../../services/OneDriveAPIs/OneDriveTypes.ts';
import { DocErrors, ErrorRoles } from '../../../hooks/useUploadReducer.ts';
import type { ImportStrategyComponentType } from '../../types.ts';
import { useFileSizeCheck } from '../../../hooks/useFileSizeCheck.ts';

import { OneDriveConfirmModal } from './OneDriveConfirmModal/OneDriveConfirmModal.tsx';
import { PermissionWarningModal } from './PermissionWarningModal.tsx';

const useImportSourceParam = createSingleQueryParamHook('importSource');

const pressableStyles = cssMap({
	root: {
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
	},
});

export enum OneDriveModals {
	NO_ACCESS = 'NO_ACCESS',
	CONFIRM = 'CONFIRM',
}

export const ImportFromOneDrive: ImportStrategyComponentType = ({
	onDocumentSelect,
	onStartStrategy,
	onDocumentSelectError,
	registerTrigger,
	children,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		closeModal: closeImportModal,
		docActions: { uploadDocFailure },
	} = useContext(ImportStateContext);
	const {
		modalStack: OneDriveModalStack,
		showModal: showOneDriveModal,
		closeModal: closeOneDriveModal,
	} = useModals<OneDriveModals>();
	const fileSizeCheck = useFileSizeCheck();
	const oneDrivePicker = useRef<OneDrivePicker | null>(null);
	const confirmModalWrapper = useRef<HTMLDivElement | null>(null);

	const pickDocument = () => {
		showOneDriveModal(OneDriveModals.CONFIRM);
	};

	const handlePickerIframeLoad = async (iframeRef, modalWrapperNode): Promise<boolean> => {
		//in case of reloading the Iframe manually, unmount the previous listeners
		oneDrivePicker.current?.unmount();

		let clientId: string | undefined;

		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('cc_feature_discovery_ifs_fe')) {
			try {
				const result = await cfetch(
					`/wiki/rest/internals/1.0/word-doc/get-onedrive-secrets?secretType=CLIENT_ID`,
				).then((res) => res.json());
				// fetch and store appId and apiKey (for upload)
				if (result) {
					clientId = result.oneDriveClientId;
				}
			} catch (err) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'office365Picker',
						action: 'fetch',
						actionSubject: 'office365Secrets',
						attributes: {
							errMessage: err.message,
						},
					},
				}).fire();
				return false;
			}
		}

		oneDrivePicker.current = new OneDrivePicker({
			multiSelect: true,
			iframe: iframeRef.current!,
			onIframeClick: handlePickerResponse,
			microsoftClientId: clientId,
		}).init();

		confirmModalWrapper.current = modalWrapperNode;
		return true;
	};
	const closeConfirmModalAndUmountPicker = () => {
		// the order is important. Unmount the picker first to send the event to the iframe inside the modal
		// and then close the modal
		// the use case https://hello.atlassian.net/wiki/spaces/CCECO/pages/1277983936/IFS+office+365+Use+cases#link_to_closing_picker
		oneDrivePicker.current?.unmount();
		setTimeout(() => {
			closeOneDriveModal(OneDriveModals.CONFIRM);
		}, 100);
	};

	const sendPickerCancelEvent = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'office365Picker',
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'office365PickerClose',
			},
		}).fire();
	};

	const sendPickerSelectEvent = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'office365Picker',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'office365PickerSelect',
			},
		}).fire();
	};

	const makeConfirmModalTransparent = () => {
		//we have to make the OneDriveConfirmModal transparent (but not close it) while the file picker is opened
		// because if we close the modal, the iframe which contains the file picker will be deleted from the page
		// and we won't receive the messages from the iframe
		//since there's no normal way to set the styles of the modal backdrop, we use the data-testid attribute to get the node
		// When the picker was launched from an inline overlay there is no modal to hide.
		if (confirmModalWrapper.current) {
			confirmModalWrapper.current.style.opacity = '0';
		}
	};

	const validateSingleUploadBeforeProgress = async (documents) => {
		if (!fileSizeCheck(documents[0])) return false;
		return documents;
	};

	const handlePickerResponse = async (filesPromise) => {
		makeConfirmModalTransparent();

		try {
			const selectedDocuments: OneDriveFileType[] | null = await filesPromise;
			closeConfirmModalAndUmountPicker();

			//the picker cancelled
			if (!selectedDocuments) {
				sendPickerCancelEvent();
				return false;
			}

			sendPickerSelectEvent();
			onDocumentSelect(selectedDocuments);
		} catch (e) {
			closeConfirmModalAndUmountPicker();
			closeImportModal(ImportModalTypes.PROGRESS);

			if (e.status === OneDriveErrorStatuses.NO_ACCESS) {
				showOneDriveModal(OneDriveModals.NO_ACCESS);
				return;
			}

			onDocumentSelectError();
		}
	};

	const exportFromMicrosoftGraph = async (doc: OneDriveFileType, isBulkUpload: boolean) => {
		try {
			return await oneDrivePicker.current!.exportFromMicrosoftGraph(doc);
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'office365Picker',
					action: 'export',
					actionSubject: 'office365Documents',
					attributes: {
						errMessage: error.message,
						isBulkUpload,
					},
				},
			}).fire();

			if (!isBulkUpload) {
				onDocumentSelectError();
				return null;
			} else {
				uploadDocFailure(doc.name, {
					type: DocErrors.FAILED_UPLOAD_ERROR,
					role: ErrorRoles.ERROR,
				});
				return null;
			}
		}
	};

	const closeModalAndSelectFile = (modalType: OneDriveModals) => {
		closeOneDriveModal(modalType);
		pickDocument();
	};

	//TODO: create base strategy component and remove the duplication
	//due to stale closure we have to keep the props in a ref, so the methods are always up to date after rerender
	// another approach is to move ImportContextStrategy into every strategy and extend it with the props.
	const strategyProps = {
		key: ActiveImports.IMPORT_FROM_ONE_DRIVE,
		folderName: formatMessage(IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_ONE_DRIVE]),
		pickDocument,
		exportFile: exportFromMicrosoftGraph,
		validateSingleUploadBeforeProgress,
	};

	const strategyPropsRef = useRef(strategyProps);
	strategyPropsRef.current = strategyProps;

	const importSourceParam = useImportSourceParam();
	const { isEnabled: isExperimentEnabled } = useTwcg2304ImportInOnboarding();

	useEffect(() => {
		// Auto-trigger OneDrive import when the user arrives via the onboarding Import CTA
		// with importSource=office365Doc in the URL (set by FooterCreateButtons → createPageContent).
		// Guard with the experiment hook so this only fires for users enrolled in the experiment.
		if (importSourceParam === ActiveImports.IMPORT_FROM_ONE_DRIVE && isExperimentEnabled) {
			onStartStrategy(strategyPropsRef);
		}
		// Run once on mount — importSourceParam is a stable query-param value
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleStrategyClick = async () => {
		onStartStrategy(strategyPropsRef);
	};

	// Expose the same entry point the card's onItemClick uses, so consumers that render
	// their own trigger (e.g. the onboarding CTA dropdown) can start the import without
	// this component owning the clickable element.
	useEffect(() => {
		registerTrigger?.({ start: () => onStartStrategy(strategyPropsRef) });
	}, [registerTrigger, onStartStrategy]);

	return (
		<>
			{OneDriveModalStack[OneDriveModals.NO_ACCESS] && (
				<PermissionWarningModal
					onClose={() => closeOneDriveModal(OneDriveModals.NO_ACCESS)}
					onSubmit={() => closeModalAndSelectFile(OneDriveModals.NO_ACCESS)}
				/>
			)}
			{OneDriveModalStack[OneDriveModals.CONFIRM] && (
				<OneDriveConfirmModal
					onIframeLoad={handlePickerIframeLoad}
					onClose={closeConfirmModalAndUmountPicker}
				/>
			)}
			<div css={pressableStyles.root}>
				{React.cloneElement(children as React.ReactElement, {
					...(children as React.ReactElement).props,
					onItemClick: (data: any) => {
						void handleStrategyClick();
						const originalOnItemClick = (children as React.ReactElement).props.onItemClick;
						if (originalOnItemClick) {
							originalOnItemClick(data);
						}
					},
				})}
			</div>
		</>
	);
};
